import { inject, Injectable, isDevMode } from '@angular/core';
import { LanguageService } from './language.service';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { LOG_MODULE_KEY } from '../constants/logs.constants';

/**
 * LocalizeService extends the functionality of LanguageService to manage
 * localization tasks within the application.
 *
 * **Purpose:**
 * This service acts as a specialized layer for handling application-specific
 * localization needs while leveraging the foundational features provided by
 * the `LanguageService` and `TranslateService`.
 *
 */
@Injectable({ providedIn: 'root' })
export class LocalizeService extends LanguageService {
  /**
   * Injects the `TranslocoLocaleService` to handle locale-specific operations.
   */
  private localeService = inject(TranslocoLocaleService);

  /**
   * Emits changes when the locale is modified, only in development mode.
   * In production, the observable will not be active.
   */
  public readonly localChange$ = isDevMode() ? this.setLocaleChange$() : null;

  /**
   * Initializes the LocalizeService by invoking the constructor of its parent
   * `LanguageService`.
   */
  constructor() {
    super();
  }

  /**
   * Sets up a subscription to the locale changes and logs the new locale to the console.
   *
   * **Details:**
   * This method listens for changes in the locale and outputs the new locale to the console.
   */
  private setLocaleChange$(): void {
    this.localeService.localeChanges$.subscribe((locale) => {
      console.log(`${LOG_MODULE_KEY} Locale changed to: ${locale}`);
    });
  }
}
